import {
	ActionService,
	LinkAuthExceptionFactory,
	EnvironmentService,
	HostBridgeService,
} from "phoenix/services";
import { AuthService } from './authService';
import { AuthAction } from './auth-service.actions';
import SettingsService from "phoenix/services/settingsService";
import { IStorageAccessor } from "util/domain/storage-types";
import { HostBridgeEvent } from "@hyland/minotaur";
import {
	AuthStateReceivedPayload,
	IdpClientConfig,
	IdpStorageKeys,
	HostBridgeEvents
} from "phoenix/domain";

const hybridClientPostLoginUri = 'sharebase://response/';

export class HybridAuthService extends AuthService {
	public readonly idpSettings:IdpClientConfig = {
		clientId: this.settingsService.idp.hybrid.clientId,
		path: this.settingsService.idp.hybrid.path,
		postLogoutUri: hybridClientPostLoginUri,
		redirectUri: hybridClientPostLoginUri,
		responseType: 'code',
		scope: this.settingsService.idp.hybrid.scope
	};

	private subscriptions = [];

	private readonly baseAuthCommand = {
		namespace: "HSIAuthorizationService",
		action: "IDPAuthorization",
		payload: {
			CLIENT_ID: this.idpSettings.clientId,
			CLIENT_SECRET: 'not_a_secret',
			ISSUER: this.idpSettings.path,
			REDIRECT_URI: this.idpSettings.redirectUri,
			RESPONSE_TYPE: this.idpSettings.responseType,
			SCOPES: this.idpSettings.scope,
		}
	};

	private readonly LogoutCommand = {
		namespace: "HSIAuthorizationService",
		action: "IDPLogout",
		payload: {
			CLIENT_ID: this.idpSettings.clientId,
			CLIENT_SECRET: 'not_a_secret',
			ISSUER: this.idpSettings.path,
			REDIRECT_URI: this.idpSettings.redirectUri,
			RESPONSE_TYPE: this.idpSettings.responseType,
			SCOPES: this.idpSettings.scope,
		}
	}

	public constructor(
		$window:ng.IWindowService,
		$http:ng.IHttpService,
		$q:ng.IQService,
		$injector,
		$location:ng.ILocationService,
		settingsService:SettingsService,
		localStorageService:IStorageAccessor,
		sessionStorageService:IStorageAccessor,
		pxActionService:ActionService,
		pxEnvironmentService:EnvironmentService,
		pxLinkAuthExceptionFactory:LinkAuthExceptionFactory,
		private readonly pxHostBridgeService:HostBridgeService
	) {
		super(
			$window,
			$http,
			$q,
			$injector,
			$location,
			settingsService,
			localStorageService,
			sessionStorageService,
			pxEnvironmentService,
			pxLinkAuthExceptionFactory
		);

		this.subscriptions.push(
			pxHostBridgeService.subscribeToEvent(HostBridgeEvents.authStateReceived,
				(response:HostBridgeEvent<AuthStateReceivedPayload>) => {
					//Store it and get user data (basically everything in the validate function assuming the token is valid)
					this.storeToken(response.payload.accessToken, 'Bearer');
					this.hydrateUserData()
						.then(() => {
							pxActionService.emit({
								type: AuthAction.AuthStateChanged
							})
						});
				}
			)
		);

		this.subscriptions.push(
			pxHostBridgeService.subscribeToEvent(HostBridgeEvents.logoutCompleted,
				(response:HostBridgeEvent<AuthStateReceivedPayload>) => {
					pxActionService.emit({
						type: AuthAction.AuthStateChanged
					})
				}
			)
		);
	}

	public $onDestroy() {
		this.subscriptions.forEach(unsubscribe => unsubscribe());
	}

	public handle401() {
		if (this.$location.path() !== '/login') {
			this.redirectPath = this.$location.path();
		}

		if (!this.rememberedEmail) {
			return this.pxHostBridgeService.execute(this.baseAuthCommand);
		}
		else {
			return this.sendToIdp(this.rememberedEmail);
		}
	}

	/**
	* @ngdoc method
	* @name pxAuthService#invalidateLogin
	* @methodOf phoenix.pxAuthService
	* @kind function
	*
	* @param {bool} inMemoryOnly Value indiciating if the localstorage values should remain
	*
	* @returns {IPromise<string>} A promise which resolves to a string for a redirect URL
	* @description
	* Clears the users Phoenix token
	*
	*/
	public invalidateLogin(inMemoryOnly:boolean = false) {
		this.authData = this.userData = this.userStats = this.authToken = this.idToken = null;
		
		if (!inMemoryOnly) {
			Object.keys(IdpStorageKeys).forEach(identifier => {
				let key = IdpStorageKeys[identifier];
				this.localStorageService.removeStoredValue(key);
			});                    
		}

		return this.pxHostBridgeService.execute(this.LogoutCommand)
			.then(() => {
				return '';
			});
	}

	public sendToIdp(emailAddress:string, rememberMe:boolean = true) {
		if (rememberMe) {
			this.rememberedEmail = emailAddress;
		}

		let command = angular.copy(this.baseAuthCommand);
		command.payload['ADDITIONAL_PARAMETERS'] = {
				acr_values: `email:${emailAddress}`
			};

		return this.pxHostBridgeService.execute(command)
			.then(angular.noop);
	}
}


// WEBPACK FOOTER //
// ./src/phoenix/services/auth/hybrid-auth-service.ts